import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const defaultSettings = {
  school_name: 'Merit Plus',
  school_subtitle: 'Мектеп жетістіктерін басқару жүйесі',
  logo_url: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_a05533bd-d761-4a3f-beb7-e7e62bdd4f88/artifacts/glvqb9sk_ChatGPT%20Image%20Aug%2013%2C%202026%20at%2002_26_31%20PM.png',
  primary_color: '#7B33FF',      // Vibrant purple (from logo)
  secondary_color: '#D4A017',    // Rich gold (shield border)
  header_bg_color: '#1B1A39',    // Dark navy-purple (logo background)
  contact_email: '',
  contact_phone: '',
  address: '',
  subdomain: '',
  is_main_domain: true,
};

const SchoolSettingsContext = createContext({
  settings: defaultSettings,
  loading: false,
  subdomain: null,
  isMainDomain: true,
  refreshSettings: () => {},
  setSchoolFromPath: () => {},
});

/**
 * Path-based routing: Get subdomain from URL path (e.g., /qyzylordaer)
 * This replaced the old subdomain-based approach (qyzylordaer.meritplusbil.org)
 */
function getSubdomainFromPath() {
  const pathname = window.location.pathname;
  
  // Check if we're on a school-specific path
  // Paths like /qyzylordaer, /shymkenter should return the subdomain
  // But paths like /admin, /teacher, /student, /login should not
  const reservedPaths = ['admin', 'teacher', 'student', 'login', 'super-admin', 'tv', 'messages', 'school-login', ''];
  
  const firstSegment = pathname.split('/')[1]?.toLowerCase();
  
  if (firstSegment && !reservedPaths.includes(firstSegment)) {
    return firstSegment;
  }
  
  return null;
}

export function SchoolSettingsProvider({ children }) {
  const [settings, setSettings] = useState(defaultSettings);
  const [loading, setLoading] = useState(false);
  const [subdomain, setSubdomain] = useState(null);
  const [isMainDomain, setIsMainDomain] = useState(true);

  const fetchSchoolSettings = useCallback(async (schoolSubdomain) => {
    if (!schoolSubdomain) {
      setSettings({ ...defaultSettings, is_main_domain: true });
      setIsMainDomain(true);
      setSubdomain(null);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/school/by-domain?subdomain=${schoolSubdomain}`);
      if (response.data && response.data.id) {
        // Backend returns school_name, school_subtitle (not name, subtitle)
        setSettings({ 
          ...defaultSettings, 
          ...response.data,
          school_name: response.data.school_name || response.data.name || defaultSettings.school_name,
          school_subtitle: response.data.school_subtitle || response.data.subtitle || defaultSettings.school_subtitle,
          is_main_domain: false 
        });
        setSubdomain(schoolSubdomain);
        setIsMainDomain(false);
      } else {
        // School not found, use defaults
        setSettings({ ...defaultSettings, is_main_domain: false });
        setSubdomain(schoolSubdomain);
        setIsMainDomain(false);
      }
    } catch (error) {
      console.error('Error fetching school settings:', error);
      // Use defaults on error but keep subdomain info
      setSettings({ ...defaultSettings, is_main_domain: false });
      setSubdomain(schoolSubdomain);
      setIsMainDomain(false);
    } finally {
      setLoading(false);
    }
  }, []);

  // Set school from path (used by SchoolLogin component)
  const setSchoolFromPath = useCallback((pathSubdomain) => {
    if (pathSubdomain && pathSubdomain !== subdomain) {
      fetchSchoolSettings(pathSubdomain);
    }
  }, [subdomain, fetchSchoolSettings]);

  // Initial load - check path for subdomain
  useEffect(() => {
    const detectedSubdomain = getSubdomainFromPath();
    if (detectedSubdomain) {
      fetchSchoolSettings(detectedSubdomain);
    } else {
      setSettings({ ...defaultSettings, is_main_domain: true });
      setIsMainDomain(true);
    }
  }, [fetchSchoolSettings]);

  const refreshSettings = useCallback(() => {
    const currentSubdomain = getSubdomainFromPath() || subdomain;
    fetchSchoolSettings(currentSubdomain);
  }, [subdomain, fetchSchoolSettings]);

  return (
    <SchoolSettingsContext.Provider value={{ 
      settings, 
      loading, 
      subdomain, 
      isMainDomain, 
      refreshSettings,
      setSchoolFromPath
    }}>
      {children}
    </SchoolSettingsContext.Provider>
  );
}

export function useSchoolSettings() {
  const context = useContext(SchoolSettingsContext);
  if (!context) {
    throw new Error('useSchoolSettings must be used within SchoolSettingsProvider');
  }
  return context;
}

export default SchoolSettingsContext;
